// Box Topper (MMP-316) — everything box-topper-print decides, with no I/O of
// its own. Import-free apart from the equally import-free _shared/auth.ts, so
// Vitest drives it directly with a fake seal store, a fake Kudos client and a
// fake relay client (src/test/boxTopperPrint.test.ts). index.ts only wires the
// real Supabase table access, Kudos RPC and relay HTTP call into it.
//
// The flow for one seal: claim its box_topper_seals row → issue the Kudos
// voucher, only when the template prints one (templateNeedsVoucher;
// external_ref = the seal's job_ref, so a retry gets the SAME voucher back)
// → POST the relay's /print-seal (job_id = the same job_ref, so the relay
// never double-prints) → record printed, or a visible failed row the tab can
// retry. The relay contract is adora-kitchen-print's README "Seal printer".

import { resolveCaller, type RequestLike } from "../_shared/auth.ts";

// --- shapes --------------------------------------------------------------------

export type SealMode = "per_order" | "per_item";
export type ColorMode = "black" | "black_red";
export type SealStatus = "pending" | "printed" | "failed";
export type SealTrigger = "auto" | "retry" | "reprint";

export interface SealTextElement {
  type: "text";
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  font?: "regular" | "bold";
  font_size: number;
  align?: "left" | "center" | "right";
  valign?: "top" | "middle" | "bottom";
  color?: "black" | "red";
  wrap?: boolean;
  line_spacing?: number;
}

export interface SealQrElement {
  type: "qr";
  x: number;
  y: number;
  size: number;
  data: string;
  ec_level?: "L" | "M" | "Q" | "H";
  quiet_zone?: number;
  color?: "black" | "red";
}

export type SealElement = SealTextElement | SealQrElement;

export type SealOrientation = "portrait" | "landscape";

export interface SealLabel {
  length_in?: number;
  /** portrait: 696 dots across the strip, length_in * 300 down it (x across,
   *  y down), printed unrotated. landscape: the old 1800 x 696 layout. */
  orientation?: SealOrientation;
  /** Turn the whole image 180 degrees before printing (which end of the strip
   *  feeds out first is only known at the first real print). */
  rotate_180?: boolean;
}

export interface SealTemplate {
  label?: SealLabel;
  color_mode?: ColorMode;
  background_png_base64?: string | null;
  elements: SealElement[];
}

export interface ItemRule {
  /** An item is boxed when its size id is at least this (size 0 / none = no box). */
  min_size: number;
  /** Item ids that never get a seal even when sized (drinks, sides, dips...). */
  skip_item_ids: number[];
}

export interface BoxTopperSettings {
  store_id: string;
  enabled: boolean;
  enabled_at: string | null;
  mode: SealMode;
  color_mode: ColorMode;
  relay_profile: string;
  template: SealTemplate;
  offer_text: string;
  offer_terms: string | null;
  validity_days: number;
  item_rule: Partial<ItemRule> | null;
}

export interface OrderRow {
  order_id: number;
  order_no: number | null;
  store_id: string | null;
  created_at: string | null;
  order_type: string | null;
  promise_time: string | null;
  customer_phone: string | null;
  items: unknown;
}

export interface SealRow {
  id: string;
  store_id: string;
  order_id: number;
  order_no: number | null;
  seal_index: number;
  seal_count: number;
  item_label: string | null;
  status: SealStatus;
  job_ref: string;
  voucher_code: string | null;
  voucher_url: string | null;
  voucher_valid_until: string | null;
  error_code: string | null;
  error: string | null;
  retryable: boolean | null;
  attempts: number;
  reprint_count: number;
  last_trigger: SealTrigger;
  created_at: string;
  last_attempt_at: string | null;
  printed_at: string | null;
}

export type NewSeal = Pick<
  SealRow,
  "store_id" | "order_id" | "order_no" | "seal_index" | "seal_count" | "item_label" | "job_ref"
>;

export type SealPatch = Partial<
  Pick<
    SealRow,
    | "status"
    | "voucher_code"
    | "voucher_url"
    | "voucher_valid_until"
    | "error_code"
    | "error"
    | "retryable"
    | "attempts"
    | "reprint_count"
    | "last_trigger"
    | "last_attempt_at"
    | "printed_at"
  >
>;

/** The table access box-topper-print needs; index.ts backs it with Supabase. */
export interface SealStore {
  getOrder(orderId: number): Promise<OrderRow | null>;
  getSettings(storeId: string): Promise<BoxTopperSettings | null>;
  /** Inserts each row unless (order_id, seal_index) already exists; returns
   *  ONLY the rows this call inserted. This is the at-most-once claim. */
  claimSeals(rows: NewSeal[]): Promise<SealRow[]>;
  getSeal(id: string, storeId: string): Promise<SealRow | null>;
  listOrderSeals(orderId: number, storeId: string): Promise<SealRow[]>;
  /** Applies `patch` only while the row still has `expect` (status and
   *  reprint_count), returning the updated row, or null if another caller
   *  moved it first. Two taps on Retry cannot both print. */
  transitionSeal(
    id: string,
    expect: { status: SealStatus[]; reprint_count: number },
    patch: SealPatch,
  ): Promise<SealRow | null>;
  updateSeal(id: string, patch: SealPatch): Promise<void>;
  deleteSeal(id: string): Promise<void>;
}

export interface KudosIssueInput {
  p_key: string;
  p_offer_details: string;
  p_terms: string | null;
  p_validity_days: number;
  p_event_name: string;
  p_external_ref: string;
}

export type KudosIssueResult =
  | { ok: true; voucher_id: string; validate_url: string; valid_until: string | null; reused: boolean }
  | { ok: false; code: string; message: string; retryable: boolean };

export interface KudosClient {
  issue(input: KudosIssueInput): Promise<KudosIssueResult>;
}

export interface RelayPrintBody {
  job_id: string;
  copies: number;
  template: SealTemplate;
  values: Record<string, string>;
  dry_run?: boolean;
}

export type RelayResult =
  | { ok: true; printed: boolean; reused: boolean; png_base64?: string; warnings: string[] }
  | { ok: false; code: string; message: string; retryable: boolean; warnings: string[] };

export interface RelayClient {
  printSeal(body: RelayPrintBody): Promise<RelayResult>;
}

export interface Deps {
  store: SealStore;
  kudos: KudosClient;
  /** Picks the relay for a store's relay_profile (null = not configured). */
  relayFor(profile: string): RelayClient | null;
  /** The store's Kudos API key for its relay_profile (null = not configured). */
  kudosKeyFor(profile: string): string | null;
  now(): Date;
}

// --- constants ------------------------------------------------------------------

/** The relay's print resolution; the canvas is 2.32 in (696 dots) across the
 *  roll by `length_in` (6 in default) along it, its orientation set per template
 *  (README "Geometry"). */
export const SEAL_DPI = 300;
/** The printable width of the 62 mm roll, in dots (2.32 in). */
export const SEAL_ROLL_DOTS = 696;
export const DEFAULT_LABEL_LENGTH_IN = 6.0;
export const SEAL_TIMEZONE = "America/Los_Angeles";
/** A runaway item list must not empty the roll on one order. */
export const MAX_SEALS_PER_ORDER = 20;
/** A seal still `pending` this long after its last attempt is presumed lost
 *  (function killed mid-print) and may be retried by hand. */
export const STALE_PENDING_MS = 2 * 60 * 1000;
export const KUDOS_EVENT_NAME = "Box Topper";
export const DEFAULT_ITEM_RULE: ItemRule = { min_size: 1, skip_item_ids: [] };
/** Stored on seal rows the browser can read, so never a raw transport error
 *  (Deno's fetch errors carry the request URL): the detail goes to the log. */
export const RELAY_UNREACHABLE_MESSAGE = "The printer relay could not be reached.";
export const VOUCHER_UNREACHABLE_MESSAGE = "The voucher service could not be reached.";
export const PRINT_NOT_STARTED_MESSAGE = "Printing did not start for this order. Tap Retry to print it.";
/** seal_index 0 is not a seal: it is the failed-row trace of an order whose
 *  automatic print died before any real seal was claimed. */
export const TRACE_SEAL_INDEX = 0;
export const OFFER_TEXT_MAX = 60;
export const OFFER_TERMS_MAX = 350;

/** Every {placeholder} box-topper-print fills. `copy`/`of` are the relay's own
 *  (always 1 of 1 here: each seal is its own job with its own voucher), so
 *  seal_no/seal_count number the seals of one order instead. */
export const SEAL_PLACEHOLDERS = [
  "order_no",
  "voucher_url",
  "voucher_code",
  "offer",
  "terms",
  "valid_until",
  "order_type",
  "pickup_time",
  "phone",
  "size",
  "seal_no",
  "seal_count",
  "copy",
  "of",
] as const;

/** The placeholders only a Kudos voucher can fill. */
export const VOUCHER_PLACEHOLDERS = ["voucher_url", "voucher_code", "valid_until"] as const;

/** Whether any text or QR data in the template uses a voucher placeholder. A
 *  template without one (a fixed QR link, such as a text-club join link) needs
 *  no Kudos voucher, so no Kudos key, no Kudos call and no voucher columns. */
export function templateNeedsVoucher(template: Pick<SealTemplate, "elements"> | null | undefined): boolean {
  const elements = Array.isArray(template?.elements) ? template!.elements : [];
  return elements.some((el) => {
    const value = el?.type === "qr" ? el.data : el?.type === "text" ? el.text : null;
    return typeof value === "string" && VOUCHER_PLACEHOLDERS.some((name) => value.includes(`{${name}}`));
  });
}

// --- auth -----------------------------------------------------------------------

export type BoxTopperCaller = { kind: "trigger" } | { kind: "user"; storeId: string; sub: string };

/** The allorders trigger (pg_net) proves itself with the Vault-held shared
 *  secret header; anything else must be a signed-in portal user with a
 *  store_id claim — the same check sms-actions uses. */
export function resolveBoxTopperCaller(req: RequestLike, secret: string | undefined): BoxTopperCaller | null {
  const provided = req.headers.get("x-box-topper-secret");
  if (secret && provided && provided === secret) return { kind: "trigger" };
  const caller = resolveCaller(req, { jobsSecret: undefined });
  return caller && caller.kind === "user" ? caller : null;
}

// --- relay profile → secret names --------------------------------------------

/** Function-secret names for a relay_profile. 'default' is the unsuffixed set. */
export function profileSecretNames(profile: string) {
  const clean = /^[a-z0-9_]{1,32}$/.test(profile) ? profile : "default";
  const suffix = clean === "default" ? "" : `_${clean.toUpperCase()}`;
  return {
    relayUrl: `BOX_TOPPER_RELAY_URL${suffix}`,
    accessClientId: `BOX_TOPPER_CF_ACCESS_CLIENT_ID${suffix}`,
    accessClientSecret: `BOX_TOPPER_CF_ACCESS_CLIENT_SECRET${suffix}`,
    kudosApiKey: `BOX_TOPPER_KUDOS_API_KEY${suffix}`,
  };
}

// --- order → seals ---------------------------------------------------------------

function num(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function firstOf(item: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") return item[key];
  }
  return undefined;
}

/** allorders.items as a list, or null when it is missing or unreadable. */
export function readItems(items: unknown): Record<string, unknown>[] | null {
  let value = items;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(value)) return null;
  return value.filter((it): it is Record<string, unknown> => !!it && typeof it === "object" && !Array.isArray(it));
}

// The relay's POS items are read tolerantly: the portal's own rows use
// snake_case (`item_id`, as get_sales_insights reads), Adora's raw objects use
// PascalCase (`SizeID`, `Quantity`, as the relay's /print-label reads).
export const itemSizeId = (it: Record<string, unknown>) => num(firstOf(it, ["size_id", "SizeID", "sizeId", "size"]));
export const itemId = (it: Record<string, unknown>) => num(firstOf(it, ["item_id", "ItemID", "itemId", "id"]));
export const itemSizeName = (it: Record<string, unknown>) => {
  const name = firstOf(it, ["size_name", "SizeName", "sizeName"]);
  return typeof name === "string" ? name.trim() : null;
};
export function itemQuantity(it: Record<string, unknown>): number {
  const q = num(firstOf(it, ["quantity", "qty", "Quantity", "QTY"]));
  return q !== null && q >= 1 ? Math.floor(q) : 1;
}

export function normalizeItemRule(rule: Partial<ItemRule> | null | undefined): ItemRule {
  const min = num(rule?.min_size);
  const skip = Array.isArray(rule?.skip_item_ids)
    ? rule!.skip_item_ids.map(num).filter((n): n is number => n !== null)
    : [];
  return { min_size: min !== null ? min : DEFAULT_ITEM_RULE.min_size, skip_item_ids: skip };
}

/** The configurable boxed-item rule: sized at or above min_size, not skipped. */
export function isBoxedItem(it: Record<string, unknown>, rule: ItemRule): boolean {
  const size = itemSizeId(it);
  if (size === null || size <= 0 || size < rule.min_size) return false;
  const id = itemId(it);
  if (id !== null && rule.skip_item_ids.includes(Math.floor(id))) return false;
  return true;
}

export interface PlannedSeal {
  seal_index: number;
  seal_count: number;
  /** Per-item mode: the size shown as {size}, e.g. "Large" or "Size 3". */
  size: string | null;
}

/**
 * How many seals an order gets. per_order: exactly one. per_item: one per
 * boxed item, quantity expanded (2 large pizzas = 2 seals), unboxed items
 * skipped — an order of only drinks gets none. An order whose item list is
 * missing or unreadable still gets one seal, because every order that shows
 * up prints. Capped at MAX_SEALS_PER_ORDER.
 */
export function planSeals(order: Pick<OrderRow, "items">, settings: Pick<BoxTopperSettings, "mode" | "item_rule">): PlannedSeal[] {
  const one: PlannedSeal[] = [{ seal_index: 1, seal_count: 1, size: null }];
  if (settings.mode !== "per_item") return one;
  const items = readItems(order.items);
  if (items === null) return one;
  const rule = normalizeItemRule(settings.item_rule);
  const sizes: (string | null)[] = [];
  for (const it of items) {
    if (!isBoxedItem(it, rule)) continue;
    const size = itemSizeName(it) || `Size ${itemSizeId(it)}`;
    for (let i = 0; i < itemQuantity(it) && sizes.length < MAX_SEALS_PER_ORDER; i++) sizes.push(size);
  }
  return sizes.map((size, i) => ({ seal_index: i + 1, seal_count: sizes.length, size }));
}

/** The one deterministic id per seal: Kudos external_ref and relay job_id. */
export const sealJobRef = (orderId: number, sealIndex: number) => `box-topper:${orderId}:${sealIndex}`;

/** A reprint must not collide with the relay's 24 h job_id replay cache. */
export const reprintJobId = (jobRef: string, reprintCount: number) =>
  reprintCount > 0 ? `${jobRef}:r${reprintCount}` : jobRef;

/** Orders from before the switch last went on never print (no backfill). */
export function isOrderEligible(order: Pick<OrderRow, "created_at" | "store_id">, settings: BoxTopperSettings | null, now: Date): boolean {
  if (!settings || !settings.enabled || !settings.enabled_at) return false;
  if (!order.store_id || order.store_id !== settings.store_id) return false;
  const created = order.created_at ? Date.parse(order.created_at) : now.getTime();
  const start = Date.parse(settings.enabled_at);
  if (Number.isNaN(start)) return false;
  return (Number.isNaN(created) ? now.getTime() : created) >= start;
}

// --- values ------------------------------------------------------------------------

export function formatPhone(raw: string | null): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : (raw ?? "").trim();
}

/** promise_time as "6:45 PM" store-local when it parses as a timestamp, else as stored. */
export function formatPickupTime(raw: string | null): string {
  if (!raw) return "";
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return raw.trim();
  return new Intl.DateTimeFormat("en-US", { timeZone: SEAL_TIMEZONE, hour: "numeric", minute: "2-digit" }).format(new Date(ms));
}

export function formatValidUntil(raw: string | null): string {
  if (!raw) return "";
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone: SEAL_TIMEZONE, month: "short", day: "numeric", year: "numeric" }).format(new Date(ms));
}

export function buildSealValues(input: {
  order: Pick<OrderRow, "order_id" | "order_no" | "order_type" | "promise_time" | "customer_phone">;
  settings: Pick<BoxTopperSettings, "offer_text" | "offer_terms">;
  seal: Pick<PlannedSeal, "seal_index" | "seal_count" | "size">;
  voucher: { code: string; url: string; valid_until: string | null };
}): Record<string, string> {
  const { order, settings, seal, voucher } = input;
  return {
    order_no: String(order.order_no ?? order.order_id),
    voucher_url: voucher.url,
    voucher_code: voucher.code,
    offer: settings.offer_text ?? "",
    terms: settings.offer_terms ?? "",
    valid_until: formatValidUntil(voucher.valid_until),
    order_type: (order.order_type ?? "").trim(),
    pickup_time: formatPickupTime(order.promise_time),
    phone: formatPhone(order.customer_phone),
    size: seal.size ?? "",
    seal_no: String(seal.seal_index),
    seal_count: String(seal.seal_count),
  };
}

/** A template is landscape only when it says so; one with no orientation is
 *  the portrait strip (the layout every new template uses). */
export const templateOrientation = (template: Pick<SealTemplate, "label">): SealOrientation =>
  template.label?.orientation === "landscape" ? "landscape" : "portrait";

/** The label canvas in printer dots for the template's own orientation:
 *  portrait is 696 across by length x 300 down; landscape is the reverse. */
export function sealCanvas(template: Pick<SealTemplate, "label">): { width: number; height: number; dpi: number; orientation: SealOrientation } {
  const lengthIn = template.label?.length_in;
  const length = Math.round((typeof lengthIn === "number" && Number.isFinite(lengthIn) && lengthIn > 0 ? lengthIn : DEFAULT_LABEL_LENGTH_IN) * SEAL_DPI);
  const orientation = templateOrientation(template);
  return orientation === "portrait"
    ? { width: SEAL_ROLL_DOTS, height: length, dpi: SEAL_DPI, orientation }
    : { width: length, height: SEAL_ROLL_DOTS, dpi: SEAL_DPI, orientation };
}

/** The template the relay receives: the template keeps its own orientation
 *  and end-for-end flip (sent only when on, so an unflipped payload is the
 *  same as before the flip existed), and the store's color mode wins over whatever the
 *  stored template says, so the owner's one switch decides the ink. */
export function templateForPrint(template: SealTemplate, colorMode: ColorMode): SealTemplate {
  const { rotate_180: rotate, ...label } = template.label ?? {};
  return {
    ...template,
    label: {
      ...label,
      length_in: label.length_in ?? DEFAULT_LABEL_LENGTH_IN,
      orientation: templateOrientation(template),
      ...(rotate === true ? { rotate_180: true } : {}),
    },
    color_mode: colorMode,
  };
}

/** The dashed fold mark on the default template: a run of dashes, since the
 *  relay draws text and QR codes only. */
const FOLD_DASHES = Array.from({ length: 28 }, () => "-").join(" ");

/** The default seal: a portrait strip (696 x 1800) that runs down the lid and
 *  folds over the front edge. The top ~190 dots are left clear for the store's
 *  logo, printed from the background artwork. Keep it identical to the column
 *  default in 20260920180000_box_topper_portrait_default.sql. */
export const DEFAULT_SEAL_TEMPLATE: SealTemplate = {
  label: { length_in: 6.0, orientation: "portrait", rotate_180: false },
  background_png_base64: null,
  elements: [
    { type: "text", text: "ONLY AT THIS LOCATION", x: 40, y: 200, w: 616, h: 44,
      font: "bold", font_size: 38, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "25075 Blue Ravine Rd, Ste 150, Folsom", x: 40, y: 248, w: 616, h: 38,
      font: "regular", font_size: 28, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "{offer}", x: 40, y: 294, w: 616, h: 264,
      font: "bold", font_size: 68, align: "center", valign: "middle", color: "red", wrap: true, line_spacing: 1.0 },
    { type: "text", text: "YOUR NEXT ORDER", x: 40, y: 566, w: 616, h: 56,
      font: "bold", font_size: 44, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "SCAN TO CLAIM", x: 40, y: 628, w: 616, h: 56,
      font: "bold", font_size: 44, align: "center", valign: "middle", color: "red", wrap: false },
    { type: "qr", x: 158, y: 694, size: 380, data: "{voucher_url}", ec_level: "M", quiet_zone: 2, color: "black" },
    { type: "text", text: "prefer to call? (916) 817-1207", x: 40, y: 1080, w: 616, h: 36,
      font: "regular", font_size: 26, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "Valid through {valid_until}", x: 40, y: 1122, w: 616, h: 44,
      font: "bold", font_size: 32, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "Order {order_no}", x: 40, y: 1170, w: 616, h: 40,
      font: "regular", font_size: 28, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: FOLD_DASHES, x: 40, y: 1226, w: 616, h: 40,
      font: "regular", font_size: 30, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "fold down over front edge", x: 40, y: 1270, w: 616, h: 36,
      font: "regular", font_size: 26, align: "center", valign: "middle", color: "red", wrap: false },
    { type: "text", text: "COME HANG WITH US IN FOLSOM!", x: 40, y: 1322, w: 616, h: 48,
      font: "bold", font_size: 34, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "DINE IN & WATCH THE GAME", x: 40, y: 1374, w: 616, h: 42,
      font: "bold", font_size: 32, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "arcade - air hockey - beer on tap - big screens", x: 40, y: 1420, w: 616, h: 34,
      font: "regular", font_size: 22, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "Locally owned & operated - Folsom proud", x: 40, y: 1458, w: 616, h: 36,
      font: "regular", font_size: 26, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "parties & catering: (916) 817-1207", x: 40, y: 1498, w: 616, h: 40,
      font: "bold", font_size: 28, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "25075 Blue Ravine Rd, Ste 150, Folsom", x: 40, y: 1544, w: 616, h: 36,
      font: "regular", font_size: 26, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "only at this location", x: 40, y: 1580, w: 616, h: 34,
      font: "regular", font_size: 24, align: "center", valign: "middle", color: "black", wrap: false },
    { type: "text", text: "{terms}", x: 40, y: 1622, w: 616, h: 150,
      font: "regular", font_size: 22, align: "center", valign: "top", color: "black", wrap: true, line_spacing: 1.15 },
  ],
};

// --- printing ----------------------------------------------------------------------

export interface SealOutcome {
  seal_id: string;
  seal_index: number;
  status: SealStatus;
  error_code?: string;
  error?: string;
}

const NO_VOUCHER = { code: "", url: "", valid_until: null };

async function printOne(
  deps: Deps,
  ctx: { order: OrderRow; settings: BoxTopperSettings },
  seal: SealRow,
  jobId: string,
  trigger: SealTrigger,
): Promise<SealOutcome> {
  const { order, settings } = ctx;
  const attemptAt = deps.now().toISOString();
  const attempts = seal.attempts + 1;
  const fail = async (code: string, message: string, retryable: boolean): Promise<SealOutcome> => {
    await deps.store.updateSeal(seal.id, {
      status: "failed",
      error_code: code,
      error: message.slice(0, 1000),
      retryable,
      attempts,
      last_attempt_at: attemptAt,
      last_trigger: trigger,
    });
    return { seal_id: seal.id, seal_index: seal.seal_index, status: "failed", error_code: code, error: message };
  };

  await deps.store.updateSeal(seal.id, { status: "pending", attempts, last_attempt_at: attemptAt, last_trigger: trigger });

  // (a) The voucher, only when the template (as it stands now, so a retry uses
  // the template at retry time) prints one; a fixed-QR seal skips Kudos
  // entirely and its voucher placeholders, if any slipped in, print empty. A
  // seal that already has one (a retry after a print failure) reuses it; Kudos
  // would return the same one for this external_ref anyway, but skipping the
  // call keeps a Kudos outage from blocking a reprint.
  const needsVoucher = templateNeedsVoucher(settings.template);
  let voucher = !needsVoucher
    ? NO_VOUCHER
    : seal.voucher_code && seal.voucher_url
      ? { code: seal.voucher_code, url: seal.voucher_url, valid_until: seal.voucher_valid_until }
      : null;
  if (!voucher) {
    const key = deps.kudosKeyFor(settings.relay_profile);
    if (!key) return fail("voucher_not_configured", "No Kudos API key is set for this store's printer.", true);
    let issued: KudosIssueResult;
    try {
      issued = await deps.kudos.issue({
        p_key: key,
        p_offer_details: settings.offer_text,
        p_terms: settings.offer_terms && settings.offer_terms.trim() !== "" ? settings.offer_terms : null,
        p_validity_days: settings.validity_days,
        p_event_name: KUDOS_EVENT_NAME,
        p_external_ref: seal.job_ref,
      });
    } catch (error) {
      console.error("[box-topper-print] Kudos request failed:", error);
      issued = { ok: false, code: "voucher_unreachable", message: VOUCHER_UNREACHABLE_MESSAGE, retryable: true };
    }
    if (issued.ok === false) return fail(issued.code, issued.message, issued.retryable);
    voucher = { code: issued.voucher_id, url: issued.validate_url, valid_until: issued.valid_until };
    await deps.store.updateSeal(seal.id, {
      voucher_code: voucher.code,
      voucher_url: voucher.url,
      voucher_valid_until: voucher.valid_until,
    });
  }

  // (b) The print.
  const relay = deps.relayFor(settings.relay_profile);
  if (!relay) return fail("relay_not_configured", "No printer relay is set for this store.", true);
  let printed: RelayResult;
  try {
    printed = await relay.printSeal({
      job_id: jobId,
      copies: 1,
      template: templateForPrint(settings.template, settings.color_mode),
      values: buildSealValues({
        order,
        settings,
        seal: { seal_index: seal.seal_index, seal_count: seal.seal_count, size: seal.item_label },
        voucher,
      }),
    });
  } catch (error) {
    console.error("[box-topper-print] Relay request failed:", error);
    printed = { ok: false, code: "relay_unreachable", message: RELAY_UNREACHABLE_MESSAGE, retryable: true, warnings: [] };
  }
  if (printed.ok === false) return fail(printed.code, printed.message, printed.retryable);

  await deps.store.updateSeal(seal.id, {
    status: "printed",
    error_code: null,
    error: null,
    retryable: null,
    printed_at: deps.now().toISOString(),
  });
  return { seal_id: seal.id, seal_index: seal.seal_index, status: "printed" };
}

export type PrintOrderResult =
  | { ok: true; skipped?: "disabled" | "before_start" | "no_boxed_items" | "already_claimed"; seals: SealOutcome[] }
  | { ok: false; error: string; status: number };

async function claimPlanned(deps: Deps, ctx: { order: OrderRow; settings: BoxTopperSettings }): Promise<SealRow[]> {
  const { order, settings } = ctx;
  const plan = planSeals(order, settings);
  if (plan.length === 0) return [];
  return deps.store.claimSeals(
    plan.map((p) => ({
      store_id: settings.store_id,
      order_id: order.order_id,
      order_no: order.order_no,
      seal_index: p.seal_index,
      seal_count: p.seal_count,
      item_label: p.size,
      job_ref: sealJobRef(order.order_id, p.seal_index),
    })),
  );
}

async function printClaimed(
  deps: Deps,
  ctx: { order: OrderRow; settings: BoxTopperSettings },
  claimed: SealRow[],
  trigger: SealTrigger,
): Promise<SealOutcome[]> {
  const seals: SealOutcome[] = [];
  for (const seal of [...claimed].sort((a, b) => a.seal_index - b.seal_index)) {
    seals.push(await printOne(deps, ctx, seal, seal.job_ref, trigger));
  }
  return seals;
}

/** Best effort: leaves a failed seal_index 0 row so an order whose automatic
 *  print died before claiming anything still shows in Recent seals, where
 *  Retry runs its real print. Never throws. */
async function recordTrace(deps: Deps, trace: { orderId: number; orderNo: number | null; storeId: string | null }) {
  if (!trace.storeId) return;
  try {
    const [row] = await deps.store.claimSeals([
      {
        store_id: trace.storeId,
        order_id: trace.orderId,
        order_no: trace.orderNo,
        seal_index: TRACE_SEAL_INDEX,
        seal_count: 1,
        item_label: null,
        job_ref: sealJobRef(trace.orderId, TRACE_SEAL_INDEX),
      },
    ]);
    if (!row) return;
    await deps.store.updateSeal(row.id, {
      status: "failed",
      error_code: "print_not_started",
      error: PRINT_NOT_STARTED_MESSAGE,
      retryable: true,
      attempts: 1,
      last_attempt_at: deps.now().toISOString(),
    });
  } catch (error) {
    console.error("[box-topper-print] Could not record the failed print:", error);
  }
}

/**
 * The trigger path: print a new order's seals, at most once each. The enabled
 * and start-point checks are repeated here (the trigger already made them) so
 * a stale pg_net request after the switch goes off prints nothing. `storeHint`
 * (the trigger's own new.store_id) only names the store for the failure trace
 * when the order row itself cannot be read.
 */
export async function printOrder(deps: Deps, orderId: number, storeHint: string | null = null): Promise<PrintOrderResult> {
  let order: OrderRow | null = null;
  let settings: BoxTopperSettings | null = null;
  try {
    order = await deps.store.getOrder(orderId);
    if (!order) return { ok: false, error: "Order not found", status: 404 };
    settings = order.store_id ? await deps.store.getSettings(order.store_id) : null;
    if (!settings || !settings.enabled) return { ok: true, skipped: "disabled", seals: [] };
    if (!isOrderEligible(order, settings, deps.now())) return { ok: true, skipped: "before_start", seals: [] };
    if (planSeals(order, settings).length === 0) return { ok: true, skipped: "no_boxed_items", seals: [] };
  } catch (error) {
    console.error("[box-topper-print] print_order failed before any seal was claimed:", error);
    await recordTrace(deps, {
      orderId,
      orderNo: order?.order_no ?? null,
      storeId: order?.store_id ?? settings?.store_id ?? storeHint,
    });
    throw error;
  }

  let claimed: SealRow[];
  try {
    claimed = await claimPlanned(deps, { order, settings });
  } catch (error) {
    console.error("[box-topper-print] print_order could not claim its seals:", error);
    await recordTrace(deps, { orderId, orderNo: order.order_no, storeId: settings.store_id });
    throw error;
  }
  if (claimed.length === 0) return { ok: true, skipped: "already_claimed", seals: [] };
  return { ok: true, seals: await printClaimed(deps, { order, settings }, claimed, "auto") };
}

export function canRetry(seal: Pick<SealRow, "status" | "last_attempt_at" | "created_at">, now: Date): boolean {
  if (seal.status === "failed") return true;
  if (seal.status !== "pending") return false;
  const since = Date.parse(seal.last_attempt_at ?? seal.created_at);
  return !Number.isNaN(since) && now.getTime() - since >= STALE_PENDING_MS;
}

async function loadContext(deps: Deps, orderId: number, storeId: string) {
  const order = await deps.store.getOrder(orderId);
  if (!order || order.store_id !== storeId) return null;
  const settings = await deps.store.getSettings(storeId);
  return settings ? { order, settings } : null;
}

export type ActionResult = { ok: true; seals: SealOutcome[] } | { ok: false; error: string; status: number };

/** Runs the order's real print for an order that has no seals yet (its
 *  automatic print never started), claiming through the same (order_id,
 *  seal_index) uniqueness, then clears the failure trace row if there is one. */
async function printMissingSeals(
  deps: Deps,
  ctx: { order: OrderRow; settings: BoxTopperSettings },
  trace: SealRow | null,
  trigger: SealTrigger,
): Promise<ActionResult> {
  const claimed = await claimPlanned(deps, ctx);
  const seals = await printClaimed(deps, ctx, claimed, trigger);
  if (trace) await deps.store.deleteSeal(trace.id);
  return { ok: true, seals };
}

/** Retry one failed (or lost pending) seal: same job id, same voucher. Works
 *  even with the feature switched off, since the owner asked for it. A failure
 *  trace (seal_index 0) instead runs the order's real print. */
export async function retrySeal(deps: Deps, storeId: string, sealId: string): Promise<ActionResult> {
  const seal = await deps.store.getSeal(sealId, storeId);
  if (!seal) return { ok: false, error: "Seal not found", status: 404 };
  if (!canRetry(seal, deps.now())) return { ok: false, error: "Only a failed seal can be retried", status: 409 };
  const ctx = await loadContext(deps, seal.order_id, storeId);
  if (!ctx) return { ok: false, error: "Order not found", status: 404 };
  const moved = await deps.store.transitionSeal(
    seal.id,
    { status: [seal.status], reprint_count: seal.reprint_count },
    { status: "pending", last_trigger: "retry", last_attempt_at: deps.now().toISOString() },
  );
  if (!moved) return { ok: false, error: "This seal is already being retried", status: 409 };
  if (moved.seal_index === TRACE_SEAL_INDEX) {
    try {
      return await printMissingSeals(deps, ctx, moved, "retry");
    } catch (error) {
      console.error("[box-topper-print] Retry could not start the print:", error);
      await deps.store.updateSeal(moved.id, {
        status: "failed",
        error_code: "print_not_started",
        error: PRINT_NOT_STARTED_MESSAGE,
        retryable: true,
        attempts: moved.attempts + 1,
        last_attempt_at: deps.now().toISOString(),
        last_trigger: "retry",
      }).catch(() => undefined);
      return { ok: false, error: PRINT_NOT_STARTED_MESSAGE, status: 502 };
    }
  }
  const outcome = await printOne(deps, ctx, moved, reprintJobId(moved.job_ref, moved.reprint_count), "retry");
  return { ok: true, seals: [outcome] };
}

/** Reprint every seal of an order on the owner's request. A printed seal gets
 *  a fresh relay job id (the relay would otherwise replay its first result for
 *  24 h) but keeps its voucher; a failed seal is simply retried. An order that
 *  has no seals yet gets them created and printed. */
export async function reprintOrder(deps: Deps, storeId: string, orderId: number): Promise<ActionResult> {
  const ctx = await loadContext(deps, orderId, storeId);
  if (!ctx) return { ok: false, error: "Order not found", status: 404 };
  const all = await deps.store.listOrderSeals(orderId, storeId);
  const seals = all.filter((s) => s.seal_index !== TRACE_SEAL_INDEX);
  if (seals.length === 0) {
    return printMissingSeals(deps, ctx, all.find((s) => s.seal_index === TRACE_SEAL_INDEX) ?? null, "reprint");
  }

  const outcomes: SealOutcome[] = [];
  for (const seal of seals.sort((a, b) => a.seal_index - b.seal_index)) {
    if (seal.status === "pending" && !canRetry(seal, deps.now())) continue;
    const reprint = seal.status === "printed";
    const nextCount = reprint ? seal.reprint_count + 1 : seal.reprint_count;
    const moved = await deps.store.transitionSeal(
      seal.id,
      { status: [seal.status], reprint_count: seal.reprint_count },
      {
        status: "pending",
        reprint_count: nextCount,
        last_trigger: reprint ? "reprint" : "retry",
        last_attempt_at: deps.now().toISOString(),
      },
    );
    if (!moved) continue;
    outcomes.push(await printOne(deps, ctx, moved, reprintJobId(moved.job_ref, nextCount), reprint ? "reprint" : "retry"));
  }
  return { ok: true, seals: outcomes };
}

// --- preview -----------------------------------------------------------------------

export const PREVIEW_JOB_ID = "box-topper-preview";

export function previewValues(input: { offer_text?: string | null; offer_terms?: string | null }, now: Date): Record<string, string> {
  const validUntil = new Date(now.getTime() + 30 * 86400000).toISOString();
  return {
    order_no: "10457",
    voucher_url: "https://earnedperks.app/validate/VCHR-PREVIEW",
    voucher_code: "VCHR-PREVIEW",
    offer: input.offer_text ?? "",
    terms: input.offer_terms ?? "",
    valid_until: formatValidUntil(validUntil),
    order_type: "Pickup",
    pickup_time: "6:45 PM",
    phone: "(209) 555-0142",
    size: "Large",
    seal_no: "1",
    seal_count: "1",
  };
}

export type PreviewResult =
  | { ok: true; png_base64: string; warnings: string[] }
  | { ok: false; error: string; code?: string; warnings?: string[]; status: number };

const upright = (template: SealTemplate): SealTemplate => {
  const { rotate_180: _flip, ...label } = template.label ?? {};
  return { ...template, label };
};

/** Render the owner's unsaved template on the relay with dry_run: a PNG at
 *  the label's real size in the template's own orientation (portrait
 *  696 x 1800), never printed, never consuming a job id. The flip is stripped so the
 *  preview always shows the design upright. */
export async function previewSeal(
  deps: Deps,
  storeId: string,
  input: { template: unknown; color_mode: unknown; offer_text?: string | null; offer_terms?: string | null },
): Promise<PreviewResult> {
  if (!input.template || typeof input.template !== "object" || !Array.isArray((input.template as SealTemplate).elements)) {
    return { ok: false, error: "template must be an object with an elements list", status: 400 };
  }
  const colorMode: ColorMode = input.color_mode === "black_red" ? "black_red" : "black";
  const settings = await deps.store.getSettings(storeId);
  const relay = deps.relayFor(settings?.relay_profile ?? "default");
  if (!relay) return { ok: false, error: "No printer relay is set for this store.", code: "relay_not_configured", status: 503 };
  let result: RelayResult;
  try {
    result = await relay.printSeal({
      job_id: PREVIEW_JOB_ID,
      copies: 1,
      dry_run: true,
      template: upright(templateForPrint(input.template as SealTemplate, colorMode)),
      values: previewValues(input, deps.now()),
    });
  } catch (error) {
    console.error("[box-topper-print] Relay preview request failed:", error);
    return { ok: false, error: RELAY_UNREACHABLE_MESSAGE, code: "relay_unreachable", status: 502 };
  }
  if (result.ok === false) return { ok: false, error: result.message, code: result.code, warnings: result.warnings, status: 502 };
  if (!result.png_base64) return { ok: false, error: "The relay returned no preview image", status: 502 };
  return { ok: true, png_base64: result.png_base64, warnings: result.warnings };
}

// --- HTTP clients (fetch injected, so tests never touch the network) ---------------

type FetchLike = (url: string, init: { method: string; headers: Record<string, string>; body: string; signal?: AbortSignal }) => Promise<{
  ok: boolean;
  status: number;
  text(): Promise<string>;
}>;

export const RELAY_TIMEOUT_MS = 25_000;
export const KUDOS_TIMEOUT_MS = 15_000;

function withTimeout(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(ms) : undefined;
}

function parseJson(text: string): Record<string, unknown> | null {
  try {
    const value = JSON.parse(text);
    return value && typeof value === "object" && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

/** Kudos outcomes that a later retry can plausibly fix on its own. */
const KUDOS_RETRYABLE = new Set(["daily_cap_reached", "too_many_attempts"]);

export function createKudosClient(opts: { url: string; anonKey: string; fetch: FetchLike }): KudosClient {
  return {
    async issue(input) {
      const res = await opts.fetch(`${opts.url.replace(/\/$/, "")}/rest/v1/rpc/issue_custom_voucher_by_key`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: opts.anonKey,
          Authorization: `Bearer ${opts.anonKey}`,
        },
        body: JSON.stringify(input),
        signal: withTimeout(KUDOS_TIMEOUT_MS),
      });
      const body = parseJson(await res.text());
      if (!res.ok) {
        const message = typeof body?.message === "string" ? body.message : `Kudos answered HTTP ${res.status}`;
        return { ok: false, code: res.status >= 500 ? "voucher_unreachable" : "voucher_rejected", message, retryable: res.status >= 500 };
      }
      if (body?.outcome === "issued" && typeof body.voucher_id === "string") {
        return {
          ok: true,
          voucher_id: body.voucher_id,
          validate_url: typeof body.validate_url === "string" ? body.validate_url : `https://earnedperks.app/validate/${body.voucher_id}`,
          valid_until: typeof body.valid_until === "string" ? body.valid_until : null,
          reused: body.reused === true,
        };
      }
      const outcome = typeof body?.outcome === "string" ? body.outcome : "unknown";
      return { ok: false, code: `voucher_${outcome}`, message: `Kudos did not issue a voucher (${outcome})`, retryable: KUDOS_RETRYABLE.has(outcome) };
    },
  };
}

export function createRelayClient(opts: { url: string; accessClientId: string; accessClientSecret: string; fetch: FetchLike }): RelayClient {
  return {
    async printSeal(body) {
      const res = await opts.fetch(`${opts.url.replace(/\/$/, "")}/print-seal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "CF-Access-Client-Id": opts.accessClientId,
          "CF-Access-Client-Secret": opts.accessClientSecret,
        },
        body: JSON.stringify(body),
        signal: withTimeout(RELAY_TIMEOUT_MS),
      });
      const json = parseJson(await res.text());
      const warnings = Array.isArray(json?.warnings) ? json!.warnings.map(String) : [];
      if (!json) {
        // Cloudflare Access answers a bad token with an HTML login page, and a
        // down tunnel with a 5xx page: neither is the relay's JSON.
        return { ok: false, code: "relay_unreachable", message: `The printer relay answered HTTP ${res.status} without its JSON reply`, retryable: true, warnings };
      }
      if (json.ok === true) {
        return {
          ok: true,
          printed: json.printed === true,
          reused: json.reused === true,
          png_base64: typeof json.png_base64 === "string" ? json.png_base64 : undefined,
          warnings,
        };
      }
      return {
        ok: false,
        code: typeof json.error === "string" ? json.error : "relay_error",
        message: typeof json.message === "string" ? json.message : `The printer relay answered HTTP ${res.status}`,
        retryable: json.retryable === true,
        warnings,
      };
    },
  };
}
